/**
 * @file WriteoffItemForm.tsx
 * @description Панель «Добавить строку» списания (phase19.13 этап 0).
 *
 * Списывается фасовка (SKU). Партию не вводим руками: после выбора фасовки
 * подтягиваем её партии (`GET /admin/wms/lots?skuId=`) и показываем списком —
 * раньше кладовщик должен был знать cuid партии наизусть.
 *
 * phase19.24 этап 1, шаг 1: панель стоит в общей карточке `DocumentAside`
 * (была своя рамка `destructive/40`), первое поле — `ScanField` с «Подбором»,
 * фокус в «Количество» переводит эффект по выбранной фасовке, а на `<lg`
 * панель свёрнута до поля скана: остальные поля появляются после выбора.
 */

import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ROW_HINT } from "./document-texts";
import { SkuSelectField } from "./SkuSelectField";
import { Button } from "../ui/button";
import { WriteoffLotField } from "./WriteoffLotField";
import { asideFieldsClass } from "../ui/aside-fields";
import { DocumentAside } from "../ui/DocumentAside";
import { ensureFieldsVisibleAboveBar } from "../ui/sticky-bar";
import { WmsFieldLabel } from "./WmsFieldLabel";
import type { WmsPickerSku } from "./WmsSkuPickerDialog";
import { lotsApi, type LotDto } from "../../lib/api-wms";
import { qtyInputProps, skuCountUnit, validateQty } from "../../lib/qty-input";

/** Тело `POST /admin/wms/writeoffs/:id/items`. */
export interface WriteoffItemPayload {
  skuId: string;
  lotId?: string;
  quantity: string;
}

interface Props {
  submitting: boolean;
  /** Партия, выбранная заранее (переход «Списать» из экрана сроков годности). */
  presetLotId?: string | null;
  /** Уже добавленные skuId — в «Подборе» серые. */
  alreadyAddedIds?: string[];
  /** Переход к уже добавленной строке документа. */
  onGoToExisting?: (skuId: string) => void;
  /** true — позиция добавлена, форма сбрасывается. */
  onSubmit: (payload: WriteoffItemPayload) => Promise<boolean>;
}

export function WriteoffItemForm({
  submitting,
  presetLotId,
  alreadyAddedIds = [],
  onGoToExisting,
  onSubmit,
}: Props) {
  const [sku, setSku] = useState<WmsPickerSku | null>(null);
  const [lots, setLots] = useState<LotDto[]>([]);
  const [lotId, setLotId] = useState(presetLotId ?? "");
  const [quantity, setQuantity] = useState("");
  const [qtyError, setQtyError] = useState<string | null>(null);
  const qtyRef = useRef<HTMLInputElement>(null);
  // Блок раскрывающихся полей: его целиком поднимаем над полосой проведения.
  const fieldsRef = useRef<HTMLDivElement>(null);
  // Списывают пачки (или граммы развеса) — единицу счёта считаем тем же
  // правилом, что сервер в задании на сборку.
  const countUnit = skuCountUnit(sku);

  // Фасовка выбрана — курсор в «Количество». Эффектом, а не колбэком поля:
  // к моменту эффекта поле уже отрисовано (а `ScanField` размонтирован).
  useEffect(() => {
    if (!sku) return;
    qtyRef.current?.focus();
    // Раскрывшиеся поля на 360px упираются в полосу «Провести списание» —
    // поднимаем их над ней (дефект повторного QA 2026-09-20).
    ensureFieldsVisibleAboveBar(fieldsRef.current, qtyRef.current);
  }, [sku]);

  // Партии выбранной фасовки. Пока фасовка не выбрана — список пуст.
  useEffect(() => {
    if (!sku) {
      setLots([]);
      return;
    }
    let cancelled = false;
    lotsApi
      .list({ skuId: sku.id })
      .then((data) => {
        if (cancelled) return;
        setLots(data);
        // Партия из другой фасовки (пришла из ссылки «Списать» на экране
        // сроков) не должна уехать в запрос — сбрасываем на автовыбор.
        setLotId((prev) => (data.some((lot) => lot.id === prev) ? prev : ""));
      })
      .catch(() => {
        if (!cancelled) setLots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [sku]);

  // Что мешает списать — одной фразой под кнопкой панели.
  const hint = !sku ? ROW_HINT.sku : !quantity.trim() ? ROW_HINT.quantity : null;

  async function handleSubmit() {
    if (!sku || !quantity.trim()) return;
    const message = validateQty(quantity, countUnit, { allowZero: false });
    if (message) {
      setQtyError(message);
      return;
    }
    setQtyError(null);
    const ok = await onSubmit({
      skuId: sku.id,
      lotId: lotId.trim() || undefined,
      quantity: quantity.trim(),
    });
    if (!ok) return;
    // Сброс фасовки монтирует поле скана заново — оно само берёт фокус.
    setSku(null);
    setLotId("");
    setQuantity("");
  }

  return (
    <DocumentAside
      title="Добавить строку"
      chrome="lg"
      titleClassName="hidden lg:flex"
      footerClassName={sku ? undefined : "hidden lg:flex"}
      footer={
        <>
          <Button
            variant="destructive"
            onClick={() => void handleSubmit()}
            disabled={!!hint || submitting}
          >
            <Plus className="size-4" />
            {/* Не «Списать»: списывает документ по кнопке «Провести списание»,
                а эта добавляет строку — одинаковый глагол читался бы как
                проведение (отклонение от п.4 ревью, см. отчёт). */}
            <span className="ml-1.5">Добавить позицию</span>
          </Button>
          {hint ? <p className="text-xs text-on-surface-variant">{hint}</p> : null}
        </>
      }
    >
      <WmsFieldLabel label="Фасовка (артикул или скан)" className="hidden lg:block">
        <SkuSelectField
          value={sku}
          onChange={setSku}
          nextFieldRef={qtyRef}
          alreadyAddedIds={alreadyAddedIds}
          onGoToExisting={onGoToExisting}
        />
      </WmsFieldLabel>

      <div ref={fieldsRef} className={asideFieldsClass(!!sku)}>
        <WriteoffLotField lots={lots} hasSku={!!sku} value={lotId} onChange={setLotId} />

        <WmsFieldLabel label={`Количество, ${countUnit}`}>
          <input
            ref={qtyRef}
            type="text"
            value={quantity}
            onChange={(e) => {
              setQuantity(e.target.value);
              setQtyError(null);
            }}
            inputMode={qtyInputProps(countUnit).inputMode}
            placeholder="0"
            className="min-h-[var(--control-h,3rem)] w-full rounded-lg bg-surface-lowest px-3 text-[length:var(--fs-field,1rem)] tabular-nums ring-ghost focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </WmsFieldLabel>
        {qtyError ? <p className="text-xs text-destructive">{qtyError}</p> : null}
      </div>
    </DocumentAside>
  );
}
